import { UseInfiniteQueryResult, useQueryClient } from 'react-query';
import type { StreamChat } from 'stream-chat';
import { dateCompare } from '@frontend/date';
import {
  useInfiniteQueryWithSetter,
  useQueryWithSetter,
  type ContextlessQueryObserverOptions,
} from '@frontend/react-query-helpers';
import { sentry } from '@frontend/tracking';
import { Message } from '../types';
import {
  getUnreadMentionsCount,
  getMentions,
  initializeStreamClient,
  getUsers,
  getConversations,
  getConversationMessages,
  getReplies,
  getThreads,
  getThreadsUnreadCount,
} from './stream-api';
import { USERS_QUERY_LIMIT, THREADS_QUERY_LIMIT } from './stream-constants';

type ClientWithUser = StreamChat & { user: NonNullable<StreamChat['user']> };
const verifyClientWithUser = (client: StreamChat | undefined) => {
  if (!client) {
    throw new Error('Stream client is not initialized');
  }
  if (!client.user?.id) {
    throw new Error('Stream client user is not connected');
  }
  return Promise.resolve(client as ClientWithUser);
};

const BaseKey = 'team-chat';
const threadsBaseKeys = [BaseKey, 'threads'];

export const queryKeys = {
  token: (orgId: string) => [BaseKey, orgId],
  client: (orgId: string, userId: string) => [BaseKey, 'client', orgId, userId],
  unreadMentionsCount: (userId: string) => [BaseKey, userId, 'unread-mentions-count'],
  mentions: (userId: string, limit: number, offset: number) => [BaseKey, userId, 'mentions', limit, offset],
  users: (orgId: string) => [BaseKey, orgId, 'users'],
  conversations: (userId: string) => [BaseKey, userId, 'conversations'],
  messages: (conversationId: string) => [BaseKey, conversationId, 'messages'],
  threads: (threadId: string) => [...threadsBaseKeys, threadId],
  threadsList: (orgId: string) => [...threadsBaseKeys, orgId, 'threads-list'],
};

export const useInvalidateAll = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries([BaseKey]);
  };
};

export const useQueryCache = () => {
  const queryClient = useQueryClient();
  const setQueryData = queryClient.setQueryData;
  return { setQueryData };
};

export const useQueryStreamClient = (
  {
    orgId,
    weaveUserId,
  }: {
    orgId: string;
    weaveUserId: string;
  },
  opts: ContextlessQueryObserverOptions = {}
) => {
  return useQueryWithSetter({
    queryKey: queryKeys.client(orgId, weaveUserId),
    queryFn: () => initializeStreamClient({ orgId, weaveUserId }),
    enabled: opts?.enabled !== false && !!orgId && !!weaveUserId,
    retry: false,
    ...opts,
  });
};

export const useQueryUnreadMentionsCount = (
  streamClient: StreamChat | undefined,
  currentUserId: string,
  opts: ContextlessQueryObserverOptions
) => {
  return useQueryWithSetter({
    queryKey: queryKeys.unreadMentionsCount(currentUserId),
    queryFn: () => verifyClientWithUser(streamClient).then((client) => getUnreadMentionsCount(client, currentUserId)),
    enabled: opts?.enabled !== false && !!streamClient?.user?.id,
    ...opts,
  });
};

export const useQueryMentions = (
  streamClient: StreamChat | undefined,
  currentUserId: string,
  limit: number,
  offset: number,
  opts: ContextlessQueryObserverOptions
) => {
  return useQueryWithSetter({
    queryKey: queryKeys.mentions(currentUserId, limit, offset),
    queryFn: () =>
      verifyClientWithUser(streamClient).then((client) => getMentions(client, currentUserId, limit, offset)),
    enabled: opts?.enabled !== false && !!currentUserId && !!streamClient?.user?.id,
    ...opts,
  });
};

export const useQueryUsers = (
  streamClient: StreamChat | undefined,
  orgId: string,
  opts: ContextlessQueryObserverOptions = {}
) => {
  return useQueryWithSetter({
    queryKey: queryKeys.users(orgId),
    queryFn: () => verifyClientWithUser(streamClient).then((client) => getUsers(client, orgId)),
    enabled: opts?.enabled !== false && !!streamClient?.user?.id,
    onError: (error) => {
      sentry.error({
        error,
        team: 'fe-bizx',
        topic: 'team-chat',
      });
    },
    ...opts,
  });
};

export const useQueryConversations = (
  streamClient: StreamChat | undefined,
  currentUserId: string | undefined,
  orgId: string,
  opts: ContextlessQueryObserverOptions & { shouldFetchHiddenConversations?: boolean } = {}
) => {
  /* The shouldFetchHiddenConversations is based on the value of feature flag. And since team chat works on org basis 
    we do not need to add this into the query Key. Since it is a temporary thing we are going to remove it after GA */
  const { shouldFetchHiddenConversations, ...queryOpts } = opts;
  return useQueryWithSetter({
    queryKey: queryKeys.conversations(currentUserId ?? 'unknown-user'),
    queryFn: () =>
      verifyClientWithUser(streamClient).then(async (client) => {
        const nonHiddenConversations = await getConversations(client, orgId);
        const hiddenConversations =
          shouldFetchHiddenConversations && (await getConversations(client, orgId, { hidden: true }));

        const dm = [
          ...nonHiddenConversations.dm,
          ...(hiddenConversations && hiddenConversations.dm ? hiddenConversations.dm : []),
        ];
        const groups = [
          ...nonHiddenConversations.groups,
          ...(hiddenConversations && hiddenConversations.groups ? hiddenConversations.groups : []),
        ];

        const unreadDms = dm
          .filter((c) => c.unreadCount > 0)
          .toSorted((c1, c2) => dateCompare(c2.lastMessageSentAt, c1.lastMessageSentAt));
        const readDms = dm
          .filter((c) => c.unreadCount === 0)
          .toSorted((c1, c2) => (!c1.name || !c2.name ? 0 : c1.name.localeCompare(c2.name)));
        const unreadGroups = groups
          .filter((c) => c.unreadCount > 0)
          .toSorted((c1, c2) => dateCompare(c2.lastMessageSentAt, c1.lastMessageSentAt));
        const readGroups = groups
          .filter((c) => c.unreadCount === 0)
          .toSorted((c1, c2) => (!c1.name || !c2.name ? 0 : c1.name.localeCompare(c2.name)));

        return { dm: [...unreadDms, ...readDms], groups: [...unreadGroups, ...readGroups] };
      }),
    enabled: opts?.enabled !== false && !!currentUserId && !!streamClient?.user?.id,
    ...queryOpts,
  });
};

export const useQueryConversationMessages = (
  streamClient: StreamChat | undefined,
  conversationId: string,
  opts: ContextlessQueryObserverOptions = {}
) => {
  return useInfiniteQueryWithSetter({
    queryKey: queryKeys.messages(conversationId),
    queryFn: ({ pageParam: cursor }) =>
      verifyClientWithUser(streamClient).then((client) =>
        getConversationMessages(client, conversationId, cursor, USERS_QUERY_LIMIT)
      ),
    getNextPageParam: (_lastPage, pages) => {
      //TODO: why?? The pages are being organized in reverse order, but I'm not doing it. So the lastPage is always the first-queried page, but the last page in the array..."
      //So Instead of looking at the lastPage, I'm going to look at the first page in the array or pages since that last-queried page is the most recent
      const hasNextPage = pages[0]?.length === USERS_QUERY_LIMIT;
      return hasNextPage ? pages[0]?.[0].id : undefined;
    },
    enabled: opts?.enabled !== false && !!streamClient?.user?.id,
    ...opts,
  });
};

export const useQueryReplies = (
  streamClient: StreamChat | undefined,
  activeConversationId: string,
  parentId: string,
  opts: ContextlessQueryObserverOptions = {}
) => {
  return useInfiniteQueryWithSetter({
    queryKey: queryKeys.threads(parentId),
    queryFn: ({ pageParam: cursor }) =>
      verifyClientWithUser(streamClient).then((client) =>
        getReplies(client, activeConversationId, parentId, USERS_QUERY_LIMIT, cursor)
      ),
    getNextPageParam: (_lastPage, pages) => {
      const hasNextPage = pages[0]?.length === USERS_QUERY_LIMIT;
      return hasNextPage ? pages[0]?.[0].id : undefined;
    },
    onError: (error) => {
      sentry.error({ error, team: 'fe-bizx', topic: 'team-chat' });
    },
    ...opts,
  });
};

export const useSetReplies = () => {
  type Data = Message[];
  type DataUpdater = (prev: UseInfiniteQueryResult<Data>['data'] | undefined) => UseInfiniteQueryResult<Data>['data'];

  const queryClient = useQueryClient();

  return (threadId: string, updater: DataUpdater) => {
    queryClient.setQueryData<UseInfiniteQueryResult<Data>['data']>(queryKeys.threads(threadId), updater);
  };
};

export const useGetRepliesQueryData = () => {
  const queryClient = useQueryClient();

  return (messageId: string): UseInfiniteQueryResult<Message[]>['data'] | undefined =>
    queryClient.getQueryData(queryKeys.threads(messageId));
};

export const useRemoveRepliesQuery = () => {
  const queryClient = useQueryClient();

  return () => queryClient.removeQueries(threadsBaseKeys);
};

export const useGetThreads = (
  streamClient: StreamChat | undefined,
  orgId: string,
  userId: string,
  opts: ContextlessQueryObserverOptions = {}
) => {
  return useInfiniteQueryWithSetter({
    queryKey: queryKeys.threadsList(orgId),
    queryFn: ({ pageParam: next }) => {
      return verifyClientWithUser(streamClient).then((client) => getThreads(client, THREADS_QUERY_LIMIT, userId, next));
    },
    getNextPageParam: (lastPage) => {
      return lastPage.next;
    },
    ...opts,
    enabled: opts.enabled !== false && !!streamClient && !!userId,
    onError: (error) => {
      sentry.error({ error, team: 'fe-bizx', topic: 'team-chat' });
    },
  });
};
export const useGetUnreadThreadsCount = (
  streamClient: StreamChat | undefined,
  orgId: string,
  opts: ContextlessQueryObserverOptions = {}
) => {
  return useQueryWithSetter({
    queryKey: [...queryKeys.threadsList(orgId), 'id-of-unread-count'],
    queryFn: () => verifyClientWithUser(streamClient).then(getThreadsUnreadCount),
    ...opts,
  });
};
